import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiHeart, FiSliders } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/shop.scss";

const fallbackImage =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 750'%3E%3Crect width='600' height='750' fill='%23f1e6d7'/%3E%3Cpath d='M180 430c55-40 88-112 136-126 42-12 68 25 104 59 32 31 61 41 71 80 9 38-21 67-64 67H220c-52 0-79-44-40-80Z' fill='none' stroke='%236d2136' stroke-width='5'/%3E%3C/svg%3E";

export function ProductCard({ product, eager = false }) {
  const { wishlist, toggleWishlist } = useContext(CartContext);
  const [loaded, setLoaded] = useState(false);
  const isFavorite = wishlist.includes(product.id);

  return (
    <article className={`product-card ${loaded ? "is-loaded" : "is-loading"}`}>
      <div className="product-card__media">
        {product.badge && (
          <span className="product-card__badge">{product.badge}</span>
        )}
        <button
          className={`product-card__heart ${isFavorite ? "is-active" : ""}`}
          type="button"
          aria-label={
            isFavorite
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
          aria-pressed={isFavorite}
          onClick={() => toggleWishlist(product.id)}
        >
          <FiHeart />
        </button>
        <Link to={`/product/${product.id}`} aria-label={`View ${product.name}`}>
          <img
            className="product-card__primary"
            src={product.images[0]}
            alt={product.name}
            loading={eager ? "eager" : "lazy"}
            onLoad={() => setLoaded(true)}
            onError={(event) => {
              event.currentTarget.src = fallbackImage;
              setLoaded(true);
            }}
          />
          <img
            className="product-card__secondary"
            src={product.images[1]}
            alt=""
            loading="lazy"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        </Link>
      </div>
      <div className="product-card__details">
        <Link to={`/product/${product.id}`}>
          <span className="product-card__category">{product.category}</span>
          <h3>{product.name}</h3>
        </Link>
        <div className="product-card__price">
          <span>{formatPrice(product.price)}</span>
          {product.compareAtPrice && (
            <del>{formatPrice(product.compareAtPrice)}</del>
          )}
        </div>
      </div>
    </article>
  );
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const requestedCategory = searchParams.get("category");
  const category =
    requestedCategory === "Shoes & Boots" ? requestedCategory : "All";
  const sort = searchParams.get("sort") || "featured";
  const search = searchParams.get("search") || "";
  const wishlistOnly = searchParams.get("wishlist") === "true";
  const { wishlist } = useContext(CartContext);

  const categories = useMemo(
    () => ["All", ...new Set(productData.map((product) => product.category))],
    []
  );

  const filteredProducts = useMemo(() => {
    let products = [...productData];
    if (category !== "All")
      products = products.filter((product) => product.category === category);
    if (search) {
      const query = search.toLocaleLowerCase("en");
      products = products.filter((product) =>
        `${product.name} ${product.category} ${product.tags.join(" ")}`
          .toLocaleLowerCase("en")
          .includes(query)
      );
    }
    if (wishlistOnly)
      products = products.filter((product) => wishlist.includes(product.id));
    if (sort === "price-low") products.sort((a, b) => a.price - b.price);
    if (sort === "price-high") products.sort((a, b) => b.price - a.price);
    if (sort === "new")
      products.sort(
        (a, b) =>
          Number(Boolean(b.badge)) - Number(Boolean(a.badge)) || b.id - a.id
      );
    if (sort === "sale")
      products = products.filter((product) => product.compareAtPrice);
    return products;
  }, [category, search, sort, wishlistOnly, wishlist]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All" || value === "featured") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  useEffect(() => {
    document.title = `${
      wishlistOnly ? "My Wishlist" : category === "All" ? "Shop" : category
    } — ${siteConfig.brandName}`;
  }, [category, wishlistOnly]);

  useEffect(() => {
    if (!requestedCategory || requestedCategory === "Shoes & Boots") return;
    const next = new URLSearchParams(searchParams);
    next.delete("category");
    setSearchParams(next, { replace: true });
  }, [requestedCategory, searchParams, setSearchParams]);

  return (
    <main className="shop">
      <header className="shop-hero shell">
        <p className="eyebrow">Footwear Collection</p>
        <h1>
          {wishlistOnly
            ? "My Wishlist"
            : category === "All"
            ? "All Footwear"
            : category}
        </h1>
        <p>
          Sneakers, loafers, boots, flats and sandals, with all prices displayed
          in pounds sterling.
        </p>
      </header>

      <div className="shop-toolbar shell">
        <button
          type="button"
          className="shop-filter-trigger"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <FiSliders /> Filter <span>{category !== "All" ? "1" : ""}</span>
        </button>
        <p>{filteredProducts.length} products</p>
        <label>
          <span>Sort by</span>
          <select
            value={sort}
            onChange={(event) => updateParam("sort", event.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="new">New Arrivals</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="sale">Last Chance</option>
          </select>
        </label>
      </div>

      <div className={`shop-filters shell ${filtersOpen ? "is-open" : ""}`}>
        {categories.map((item) => (
          <button
            key={item}
            type="button"
            className={item === category ? "is-active" : ""}
            onClick={() => updateParam("category", item)}
          >
            {item}
          </button>
        ))}
      </div>

      {filteredProducts.length > 0 ? (
        <section className="product-grid shell" aria-label="Product list">
          {filteredProducts.map((product, index) => (
            <ProductCard key={product.id} product={product} eager={index < 4} />
          ))}
        </section>
      ) : (
        <section className="shop-empty shell">
          <h2>No products found</h2>
          <p>
            {wishlistOnly
              ? "Add favourites using the heart icon."
              : "Try another category or reset the filters."}
          </p>
          <button
            className="btn btn--outline"
            type="button"
            onClick={() => setSearchParams({})}
          >
            View the full shop
          </button>
        </section>
      )}

      <section className="shop-story shell">
        <div>
          <p className="eyebrow">An easy-to-browse collection</p>
          <h2>Styles for every occasion</h2>
        </div>
        <p>
          Compare silhouettes, materials, available sizes and fit guidance to
          find the right pair for your everyday life.
        </p>
      </section>
    </main>
  );
}

export default Shop;
